import { EducationUser, User } from "@microsoft/microsoft-graph-types";
import { CommandBarButton, MessageBarType, } from "office-ui-fabric-react";
import React, { Component, Fragment } from "react";
import ApiService from "../../ApiService";
import * as _ from "lodash";
import moment from "moment";
import * as MicrosoftGraph from "@microsoft/microsoft-graph-client";
import Actions from "../../../redux/Actions";
import DispatchProps from "../../../utils/DispatchProps";
import { IRootState } from "../../../store";
import { connect } from "react-redux";
import StudentAssignmentStatusPill from "./StudentAssignmentStatusPill";
import { StudentAssignment } from "../../../models/studentAssignmentSearch/StudentAssignment";
import { ResourceInformation } from "../../../models/studentAssignmentSearch/ResourceInformation";
import { ClassWithSubject } from "../../../models/studentAssignmentSearch/ClassWithSubject";
import { openFileDownload } from "../../../utils/Utils";
import LoadingSection from "../../LoadingSection";
import {StudentAssignmentSearchOutcome} from "../../../models/studentAssignmentSearch/StudentAssignmentSearchOutcome";
import SchoolMenu from "../../SchoolMenu";
import SchoolPageTitle from "../../SchoolPageTitle";

interface StudentAssignmentProps {
    tenantId: string;
    schoolCode: string;
    classId: string;
    userId: string;
    assignmentId: string;
    apiService: ApiService;
    graphClient: MicrosoftGraph.Client;
    userIsCdb: boolean;
}

interface StudentAssignmentState {
    assignment?: StudentAssignment;
    teacherForAssignment?: User;
    studentForAssignment?: EducationUser;
    classInfo?: ClassWithSubject;
    submissionResources?: ResourceInformation[];
    loading: boolean;
    outcome?: StudentAssignmentSearchOutcome;
}

class StudentAssignmentPage extends Component<StudentAssignmentProps & DispatchProps, StudentAssignmentState> {

    constructor(props: StudentAssignmentProps & DispatchProps) {
        super(props);
        this.state = {
            loading: false
        };
    }

    async componentDidMount(): Promise<void> {
        if (this.props.tenantId && this.props.schoolCode && this.props.assignmentId && this.props.classId && this.props.userId) {
            try {
                this.setState({ loading: true });

                var assignment = await this.props.apiService.getAssignment(this.props.tenantId, this.props.schoolCode, this.props.classId, this.props.assignmentId, this.props.userId);

                this.setState({
                    assignment: assignment
                })

                this.getTeacherUserFromGraph()

                var student = await this.props.apiService.getUser(this.props.tenantId, this.props.schoolCode, this.props.userId)
                this.setState({ studentForAssignment: student })

                var classInfo = await this.props.apiService.getClassWithSubject(this.props.tenantId, this.props.schoolCode, this.props.classId);
                this.setState({ classInfo: classInfo })

                if (this.state.assignment) {
                    var latestSubmission = this.state.assignment?.submissions[0]

                    if (latestSubmission) {
                        var submissionResources = await this.props.apiService.getSubmissionResourcesForAssignmentSubmission(this.props.tenantId, this.props.schoolCode, this.props.assignmentId, this.props.classId, latestSubmission.submissionId);
                        this.setState({ submissionResources: submissionResources });

                        var outcome = await this.props.apiService.getOutcomeForAssignmentSubmission(this.props.tenantId, this.props.schoolCode, this.props.assignmentId, this.props.classId, latestSubmission.submissionId);
                        this.setState({ outcome: outcome });
                    }
                }

                this.setState({ loading: false });

            }
            catch (e) {
                console.log(e);
                this.props.dispatch(Actions.showMessageBar(`Error loading assignment information. If this problem persists please contact support`, MessageBarType.error));

                this.setState({ loading: false });
            }
        }
    }

    async componentDidUpdate(prevProps: Readonly<StudentAssignmentProps & DispatchProps>, prevState: any) {
    }

    async componentWillUnmount() {
        this.setState({ assignment: undefined, submissionResources: undefined, teacherForAssignment: undefined })
    }

    async getTeacherUserFromGraph() {

        const filterString = `id eq '${this.state.assignment?.createdByUserId}'`;

        let user: User[] = [];

        if (this.props.userIsCdb) {
            var userFromGraph = await this.props.apiService.searchGraphUsersAdmin(this.props.tenantId, filterString);

            user = userFromGraph;
        }
        else {
            let reqBuilder = this.props.graphClient.api("/users").filter(filterString);

            const response = await reqBuilder.get();

            user = response.value;
        }

        this.setState({ teacherForAssignment: user[0] as User });
    }

    private async downloadResource(resourceInformation: ResourceInformation, isSubmissionResource: boolean) {
        try {
            let result;
            if (isSubmissionResource) {
                result = await this.props.apiService.downloadEducationSubmissionResource(this.props.tenantId, this.props.schoolCode, resourceInformation.assignmentId, resourceInformation.classId, resourceInformation.submissionId, resourceInformation.resourceId);
            }
            else {
                result = await this.props.apiService.downloadEducationAssignmentResource(this.props.tenantId, this.props.schoolCode, resourceInformation.assignmentId, resourceInformation.classId, resourceInformation.resourceId);
            }

            await openFileDownload(result)

            this.props.dispatch(Actions.showMessageBar(`Downloading Resource`, MessageBarType.success));
        }
        catch (e) {
            console.log(e);
            this.props.dispatch(Actions.showMessageBar(`Error downloading resource. If this problem persists please contact support. ${e.message}`, MessageBarType.error));
        }
    }

    private async followResourceLink(resourceInformation: ResourceInformation) {
        window.open(resourceInformation.webLink, "_blank");
    }

    private getResourceLink(resourceInformation: ResourceInformation, isSubmissionResource: boolean) {
        switch (resourceInformation.resourceType) {
            case "#microsoft.graph.educationWordResource":
            case "#microsoft.graph.educationExcelResource":
            case "#microsoft.graph.educationPowerPointResource":
            case "#microsoft.graph.educationMediaResource":
            case "#microsoft.graph.educationFileResource":
                return (<span onClick={() => this.downloadResource(resourceInformation, isSubmissionResource)}>Download</span>);

            case "#microsoft.graph.educationLinkResource":
                return (<span onClick={() => this.followResourceLink(resourceInformation)}>Link</span>);

            default:
                return undefined;
        }
    }

    private getResourceType(resourceInformation: ResourceInformation) {
        switch (resourceInformation.resourceType) {
            case "#microsoft.graph.educationWordResource":
                return "Microsoft Word File";
            case "#microsoft.graph.educationExcelResource":
                return "Microsoft Excel File";
            case "#microsoft.graph.educationPowerPointResource":
                return "Microsoft Power Point File";
            case "#microsoft.graph.educationMediaResource":
                return "Media Resource";
            case "#microsoft.graph.educationFileResource":
                return "File";
            case "#microsoft.graph.educationLinkResource":
                return "External Link";
            case "#microsoft.graph.educationExternalResource":
                return "External Resource";
            case "#microsoft.graph.educationTeamsAppResource":
                return "Teams Resource";
            default:
                return "Submission Resource";
        }
    }

    private renderResouceInfo(resourceInformation: ResourceInformation, isSubmissionResource: boolean) {
        return (
            <div className="assignment-resource" key={resourceInformation.resourceId}>
                <div className="title">{resourceInformation.displayName}</div>
                <div className="type">{this.getResourceType(resourceInformation)}</div>
                <div className="link">{this.getResourceLink(resourceInformation, isSubmissionResource)}</div>
            </div>
        );
    }

    public render(): JSX.Element {
        const teamsChatLink = `https://teams.microsoft.com/l/chat/0/0?users=${this.state.teacherForAssignment?.mail}`
        const teamsChatButton = <a href={teamsChatLink}><CommandBarButton text="Chat" iconProps={{ iconName: "SkypeMessage" }} /></a>

        const mailToLink = `mailto:${this.state.teacherForAssignment?.mail}?subject=${this.state.assignment?.displayName} Assignment`
        const emailButton = <a href={mailToLink}><CommandBarButton text="Send Email" iconProps={{ iconName: "Mail" }} /> </a>

        const dueDate = moment(this.state.assignment?.dueDateTime).format("DD MMMM YYYY HH:mm");

        const instructions = this.state.assignment?.instructions || "No instructions";
        const instructionsDocument = (new DOMParser()).parseFromString(instructions, "text/html");


        const feedback: string = this.state.outcome?.feedback || "No feedback";
        const feedbackDocument = (new DOMParser()).parseFromString(feedback, "text/html");

        return (
            <Fragment>
                <SchoolPageTitle tenantId={this.props.tenantId} schoolCode={this.props.schoolCode} title="Student Assignment Search" reverse={true} />
                <SchoolMenu prefix="Student Assignment Search" prefixLink={`/manage/${this.props.tenantId}/${this.props.schoolCode}/student-assignment-search`} suffix={this.state.studentForAssignment?.displayName} />

                <div className={"cdb-admin-content cdb-admin-content--column"}>
                    <div className="drill-down-page">
                        <LoadingSection text="Loading Assignment" dependantObjects={[this.state.assignment]} defaultMessage="Failed to load Assignment">
                            <div className="drill-down-page__main-info">
                                <div className="flex-item--flex-1 flex--justify-content-flex-start">
                                    <h2>{this.state.assignment?.displayName}</h2>
                                    <span>
                                        Due {dueDate}
                                        <br />
                                        <LoadingSection loading={this.state.loading} text="Loading Student" dependantObjects={[this.state.studentForAssignment]}>
                                            Assigned to {this.state.studentForAssignment?.displayName}
                                            <br />
                                        </LoadingSection>
                                        <LoadingSection loading={this.state.loading} text="Loading Teacher" dependantObjects={[this.state.teacherForAssignment]}>
                                            Set by {this.state.teacherForAssignment?.displayName} {emailButton} {teamsChatButton}
                                            <br />
                                        </LoadingSection>
                                        <LoadingSection loading={this.state.loading} text="Loading Class" dependantObjects={[this.state.classInfo]}>
                                            {this.state.classInfo?.className} - {this.state.classInfo?.subject}
                                        </LoadingSection>
                                    </span>
                                </div>
    
                                <div className="drill-down-page__submission-info">
                                    <StudentAssignmentStatusPill assignment={this.state.assignment} submission={this.state.assignment?.submissions[0]}></StudentAssignmentStatusPill>
                                    <LoadingSection loading={this.state.loading} text="Loading Score" dependantObjects={[this.state.outcome, this.state.assignment?.grading?.maxPoints]} defaultMessage="No Score">
                                        <h4>{this.state.outcome?.points} / {this.state.assignment?.grading?.maxPoints} </ h4>
                                    </LoadingSection>
                                </div>
                            </div>
    
                            <div className="drill-down-page__assignment-information-area">
                                <div className="flex-item--flex-1 flex--justify-content-flex-start" style={{ paddingRight: "1rem" }}>
                                    <h3>Instructions</h3>
                                    <div dangerouslySetInnerHTML={{ __html: instructionsDocument.body.innerHTML }}></div>
                                </div>
    
                                <div className="flex-item--flex-1 flex--justify-content-flex-end">
                                    <h3>Feedback</h3>
                                    <LoadingSection loading={this.state.loading} text="Loading Feedback" dependantObjects={[this.state.outcome]} defaultMessage="No feedback">
                                        <div dangerouslySetInnerHTML={{__html: feedbackDocument.body.innerHTML}}></div>
                                    </LoadingSection>
                                </div>
                            </div>
    
                            <div className="drill-down-page__assignment-information-area">
                                <div className="flex-item--flex-1 flex--justify-content-flex-start">
                                    <h3>Assignment Resources</h3>
                                    <div>{this.state.assignment?.resources?.map(r => this.renderResouceInfo(r, false))}</div>
                                </div>
    
                                <div className="flex-item--flex-1 flex--justify-content-flex-start">
                                    <h3>Submission Resources</h3>
                                    <LoadingSection loading={this.state.loading} text="Loading Submission resources" dependantObjects={[this.state.submissionResources]} defaultMessage="No submission resources" defaultOnEmpty={true}>
                                        <div>{this.state.submissionResources?.map(r => this.renderResouceInfo(r, true))}</div>
                                    </LoadingSection>
                                </div>
                            </div>
                        </LoadingSection>
                    </div>
                </div>
            </Fragment>
        );
    }
}

export default connect(
    (state: IRootState, ownProps: { match: any }): StudentAssignmentProps => {
        return {
            tenantId: ownProps.match.params.tenantId,
            schoolCode: ownProps.match.params.schoolCode,
            classId: ownProps.match.params.classId,
            assignmentId: ownProps.match.params.assignmentId,
            userId: ownProps.match.params.userId,
            apiService: state.auth.apiService,
            graphClient: state.auth.graphClient,
            userIsCdb: state.auth.userIsCdbEmployee
        };
    }
)(StudentAssignmentPage);